/** Display helpers shared by the CRM surfaces. */

/**
 * The API serialises date-only columns as a full ISO timestamp
 * ("1990-08-12T00:00:00.000Z"). Slicing the date part is deliberate: passing it
 * through `new Date()` and formatting locally drifts a day for anyone west of
 * UTC, which is how birthdays end up wrong.
 */
export function toDateInputValue(isoDate: string | null): string {
  return isoDate ? (isoDate.split('T')[0] ?? '') : '';
}

/** Renders a date-only value without applying a timezone. */
export function formatDateOnly(isoDate: string | null): string {
  const value = toDateInputValue(isoDate);
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return '—';
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).toLocaleDateString(
    undefined,
    { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' },
  );
}

/** Timestamps are true instants, so these are safe to localise. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Em dash for an absent value, so empty cells read as deliberate. */
export function orDash(value: string | null | undefined): string {
  return value && value.trim() !== '' ? value : '—';
}

/*
 * `formatDateOnly` and `toDateInputValue` above also handle the catalog's bare
 * 'YYYY-MM-DD' dates unchanged: with no 'T' to split on, the whole string
 * survives, and the formatter builds a UTC date and renders it in UTC. That is
 * exactly what those columns need — they carry no time and no timezone, and
 * localising them drifts a day for anyone west of Greenwich.
 */

/**
 * Groups a decimal string for display without ever touching a float.
 *
 * `unitPrice` arrives as an exact DECIMAL(12,2) string ("18000.00"). Passing it
 * through `Number()` to format it loses that exactness — `Number("18000.10") *
 * 100` is not 1801000 — so the integer part is grouped as text and the fraction
 * is copied through verbatim.
 *
 * `null` renders as an em dash rather than 0: an unpriced product and a product
 * that genuinely costs 0.00 are different facts, and the API is careful to keep
 * them apart.
 */
export function formatDecimalString(value: string | null): string {
  if (value === null) return '—';
  const trimmed = value.trim();
  if (trimmed === '') return '—';

  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '', fraction] = unsigned.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = fraction === undefined ? grouped : `${grouped}.${fraction}`;
  return negative ? `-${body}` : body;
}

/** Pluralises a count with its own noun. "1 intake", "3 intakes". */
export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Renders a deliverable's `pending` count, which is **signed**.
 *
 * `pending` is `promised - completed`, computed server-side on every read, and
 * `completed` has no upper bound: overdelivery is real and deliberately
 * permitted, so `completed > promised` is a valid state and `pending` is then
 * negative. Never `Math.max(0, pending)` — that hides real overdelivery from the
 * people who most need to see it.
 *
 * A minus sign alone reads as an error rather than as good news, so the sign is
 * spelled out in words instead: `3` -> "3 pending", `0` -> "On track",
 * `-2` -> "2 over". A progress bar may clamp the *bar*, but the number this
 * returns must still be shown.
 */
export function formatPending(pending: number): string {
  if (pending === 0) return 'On track';
  if (pending < 0) return `${-pending} over`;
  return `${pending} pending`;
}

/**
 * Renders `durationDays`, the server's **inclusive** span between `startDate`
 * and `endDate`.
 *
 * `null` unless both dates are set — one date alone is `null`, not a partial
 * count and not `0`. When both are set the count includes both endpoints, so a
 * same-day deliverable is `1` day, not `0`; it is `(end - start) + 1`.
 *
 * Display the server's value and never recompute it: a naive date subtraction
 * is off by one against every figure the API reports.
 */
export function formatDuration(durationDays: number | null): string {
  if (durationDays === null) return '—';
  return pluralise(durationDays, 'day');
}

/**
 * A list as a sentence: `a`, `a and b`, `a, b and c`.
 *
 * Existed as three byte-identical private copies — in the company form, the
 * integrations page and the Xero sales-defaults fields — which is two more than
 * a four-line function has any business having. It reads inside user-facing
 * warning copy ("Saving clears X and Y…"), so the three drifting apart would
 * have meant one screen listing things differently from another for no reason a
 * reader could see.
 *
 * No Oxford comma, matching the house voice elsewhere in the app.
 */
export function joinList(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * What to call a company on a document a customer reads.
 *
 * `shortName` when someone has set one, `name` otherwise — one resolver so the
 * quote form and the invoice form cannot answer differently for the same
 * company. Two independent `company.name.trim()` calls is how the same sale
 * ends up titled two ways across a quote and the invoice raised from it.
 *
 * ⚠️ **The fallback is the normal path, not the exception.** `shortName` is
 * null on every company in production today, so this returns `name` for all of
 * them, at up to 81 characters. Callers with a length limit must handle a long
 * answer; they must not ask this to shorten one.
 *
 * ⚠️ **Nothing here derives a short name, and nothing may be added that does.**
 * The transforms that suggest themselves are wrong on real rows: trimming
 * "University of California, Berkeley - Haas School of Business (MFE program)"
 * at its first comma yields "University of California", which is a different
 * institution — there are ten campuses and the trim removes the word saying
 * which. See `Company.shortName` for the measured distribution.
 *
 * Blank-but-present is treated as unset: a `shortName` of spaces is somebody
 * having cleared the field, and falling back is what they meant.
 */
export function companyDocumentName(company: { name: string; shortName: string | null }): string {
  const short = company.shortName?.trim() ?? '';
  return short === '' ? company.name.trim() : short;
}

/**
 * How a quote or invoice is TITLED on screen: its subject, followed by Xero's
 * number once Xero has issued one.
 *
 * The owner's rule is "company short name + Xero number", and this composes the
 * second half **at render time rather than storing it**. The number already
 * lives in its own column (`xeroQuoteNumber` / `xeroInvoiceNumber`) and already
 * arrives on the same read as the subject, so joining them here costs nothing
 * and writes nothing.
 *
 * ⚠️ **DO NOT REINSTATE THE WRITE-BACK VERSION. It was built, and removing it
 * was the point.** Appending the number to the stored `subject` after a push
 * looks equivalent and is not, for a reason that lives one layer down in the
 * backend: a write to `subject` calls `markContentChanged` on the Xero link,
 * because the subject is a field that reaches Xero. The sequence is push
 * (stamps `lastSyncedAt`), then PATCH (stamps `contentChangedAt` strictly
 * later) — so **every pushed document would come back reading "there are
 * unsent changes", permanently, until somebody pushed it a second time.**
 *
 * And that flag would not have been lying: after such a write Xero holds
 * "Arizona State University" while we hold "Arizona State University QU-0042",
 * which is a real divergence in a field Xero reads. Suppressing the flag would
 * therefore have been the wrong fix; not creating the divergence is the right
 * one. Converging it instead would cost a second push — 2+ Xero calls per
 * document, on a budget shared across the whole office.
 *
 * Composing here avoids all of it, and buys one thing the stored version could
 * never do: a document **retitled later keeps its number**, because the number
 * is not embedded in a string somebody edits.
 *
 * Returns the subject unchanged when there is no number yet — before a push
 * there is nothing to show, and a title ending in a dangling separator reads as
 * a rendering fault.
 */
export function documentDisplayTitle(subject: string, xeroNumber: string | null): string {
  const number = (xeroNumber ?? '').trim();
  const current = subject.trim();
  if (number === '' || current === '') return subject;
  /*
   * Guarded against a subject that already contains the number — a document
   * created before this rule, or one somebody titled by hand from what they saw
   * on screen. Printing "… QU-0042 QU-0042" would read as a bug in the number
   * rather than in the title.
   */
  if (current.includes(number)) return current;
  return `${current} ${number}`;
}

/**
 * Joins names into a readable English list — `a`, `a and b`, `a, b and c`.
 *
 * Written for the "the board is not narrowed by the … filters" notices on
 * `/quotes` and `/invoices`, which each name a GROWING set of filters the kanban
 * schema cannot take.
 *
 * ⚠️ **IT EXISTS BECAUSE THE ALTERNATIVE WAS A NESTED TERNARY PER PAGE**, and
 * that shape is what fails silently as the set grows: two filters need two
 * branches, three need seven, and the person adding the fourth filter updates
 * the query builder and misses one arm. Nothing renders wrong — the notice
 * simply stops naming a filter that is not being applied, which is the exact
 * "it looks like it worked" failure the notice exists to prevent.
 *
 * ⚠️ **No Oxford comma, and no `Intl.ListFormat`.** The house voice does not use
 * one, and `Intl.ListFormat` would follow the VIEWER's locale — so this sentence
 * would punctuate differently for different staff while the rest of the page
 * stayed in English. A formatter that localises half a sentence is worse than
 * one that localises none of it.
 *
 * An empty list returns `''`; every caller already guards on `length > 0`,
 * because a notice with no names in it is a notice about nothing.
 */
export function formatList(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0]!;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]!}`;
}
